import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProtectedRoute from "components/ProtectedRoute";
import MainLayout from "components/MainLayout";
import { useAuth } from "contexts/AuthContext";

// Import pages
import AnalyticsDashboard from './pages/analytics-dashboard';
import AdminDashboard from './pages/admin-dashboard';
import AdminHomeDashboard from './pages/admin-home-dashboard';
import EmployeeHomeDashboard from './pages/employee-home-dashboard';
import JobPostingsManagement from './pages/job-postings-management';
import CandidateProfile from './pages/candidate-profile';
import LoginPage from './pages/login';
import CandidatePipeline from './pages/candidate-pipeline';
import Careers from './pages/careers';
import CheckoutSuccess from './pages/checkout-success';

// Marketing Site Pages
import LandingPage from './pages/landing';
import PricingPage from './pages/pricing';
import PrivacyPolicyPage from './pages/legal/privacy';
import TermsOfServicePage from './pages/legal/terms';
import LegalTermsPrivacyPage from './pages/legal-terms-privacy';
import OnboardingPage from './pages/onboarding';

// HR Platform Pages - New Intime HR System
import EmployeeDirectory from './pages/employee-directory';
import EmployeeOnboarding from './pages/employee-onboarding';
import TimeOffManagement from './pages/time-off-management';
import PerformanceManagement from './pages/performance-management';

// Define public paths (equivalent to Next.js middleware PUBLIC_PATHS)
const PUBLIC_PATHS = [
  "/", 
  "/pricing", 
  "/legal/terms", 
  "/legal/privacy",
  "/legal-terms-privacy",
  "/careers",
  "/login",
  "/onboarding",
  "/checkout-success",
  "/api/billing/webhooks/stripe"
];

// Root redirect component with middleware-like logic
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If authenticated user hits root, redirect to /admin-home-dashboard (main dashboard)
  return user ? <Navigate to="/admin-home-dashboard" replace /> : <LandingPage />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Root route with conditional redirect (middleware-like behavior) */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Public Marketing Routes - explicitly public */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal-terms-privacy" element={<LegalTermsPrivacyPage />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          
          {/* Protected HR Platform Routes with Sidebar Navigation */}
          <Route path="/admin-home-dashboard" element={
            <ProtectedRoute>
              <MainLayout>
                <AdminHomeDashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/employee-home-dashboard" element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeHomeDashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/employee-directory" element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeDirectory />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/employee-onboarding" element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeOnboarding />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/time-off-management" element={
            <ProtectedRoute>
              <MainLayout>
                <TimeOffManagement />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/performance-management" element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceManagement />
              </MainLayout>
            </ProtectedRoute>
          } />
          
          {/* Protected ATS Application Routes with Sidebar Navigation */}
          <Route path="/app" element={
            <ProtectedRoute>
              <MainLayout>
                <CandidatePipeline />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/app/analytics" element={
            <ProtectedRoute>
              <MainLayout>
                <AnalyticsDashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/app/admin" element={
            <ProtectedRoute>
              <MainLayout>
                <AdminDashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/app/jobs" element={
            <ProtectedRoute>
              <MainLayout>
                <JobPostingsManagement />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/app/candidate/:id" element={
            <ProtectedRoute>
              <MainLayout>
                <CandidateProfile />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/app/pipeline" element={
            <ProtectedRoute>
              <MainLayout>
                <CandidatePipeline />
              </MainLayout>
            </ProtectedRoute>
          } />
          
          {/* Redirect legacy routes to new protected structure */}
          <Route path="/analytics-dashboard" element={<Navigate to="/app/analytics" replace />} />
          <Route path="/admin-dashboard" element={<Navigate to="/app/admin" replace />} />
          <Route path="/job-postings-management" element={<Navigate to="/app/jobs" replace />} />
          <Route path="/candidate-profile" element={<Navigate to="/app/candidate/1" replace />} />
          <Route path="/candidate-pipeline" element={<Navigate to="/app/pipeline" replace />} />
          
          {/* API routes handler - simulate Next.js API route protection */}
          <Route 
            path="/api/*" 
            element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">API Endpoint</h2>
                  <p className="text-gray-600">This would be handled by your backend API.</p>
                </div>
              </div>
            } 
          />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;